import { userSession } from 'state/session';
import { tenantConfig } from 'utils/tenant';
import { ISyncTelemetrySnapshot, recordListenerGuardTrip, syncTelemetrySnapshot } from 'state/session/syncTelemetry';
import { IVoipQualitySnapshot, voipQualitySnapshot } from 'state/session/voipQuality';
import { EventEmitter, setListenerGuardReporter } from 'utils/events';
import { isDebugMode } from 'utils/ui/debug';

/**
 * Browser-console diagnostics for session-sync health (AVS-4332) and VOIP quality (AVS-4419).
 *
 * Deliberately a SEPARATE module from `state/session/syncTelemetry`. The counter store may import
 * nothing but `utils/log`, because `utils/network` imports it and `utils/network` ↔ `state/session`
 * is a real require cycle. Reading live listener counts needs `state/session` and `utils/tenant`,
 * so that half lives here — above both — and nothing in the network path imports this file.
 *
 * Nothing is sent anywhere by this module: the numbers exist to be read from the console. The
 * `voipQuality` block is the one set that also leaves the device — as flat numbers on the existing
 * session analytics event (`utils/ui/analytics.ts`), never from here.
 */

/** Live listener counts, so a leak is visible next to the counters that a leak tends to move. */
interface IListenerCounts {
  userSessionChange: number;
  tenantConfigChange: number;
}

export interface ISyncTelemetryReport extends ISyncTelemetrySnapshot {
  listeners: IListenerCounts;
  /** AVS-4419: the session-scoped VOIP counters — the same numbers the SESSION_ENDED analytics event ships. */
  voipQuality: IVoipQualitySnapshot;
}

function changeListenerCount(emitter: EventEmitter): number {
  return emitter.listenerCount('change');
}

/**
 * Read the current counters plus live listener counts.
 *
 * Gated INSIDE the accessor rather than at assignment time — which is the difference that makes it
 * usable. `isDebugMode()` evaluates the `?dev` URL parameter on every call, so gating at assignment
 * (the pattern used by `window.userSession` and `window.voipRtcConnection`) would mean a session
 * already misbehaving can never be inspected: you would have to reload with `?dev` and destroy the
 * state you were trying to diagnose. Gating here means `history.replaceState` is enough to open the
 * probe mid-session, no reload.
 *
 * Returns `undefined` (with a console hint) when gated, rather than throwing.
 *
 * Note the gate is broad: `isDebugMode()` is true for `MODE === 'development'`, `?test`, `?dev`, OR
 * any `STAGE !== 'prod'`. So off-prod the probe is readable by default and `?dev=1` is only the
 * PROD opener — the console hint below describes that prod path.
 */
export function readSyncTelemetry(): ISyncTelemetryReport | undefined {
  if (!isDebugMode()) {
    console.info(
      '[SyncTelemetry] gated. Enable without losing session state:\n' +
        "  history.replaceState(null, '', location.href + (location.search ? '&' : '?') + 'dev=1')\n" +
        '  window.syncTelemetry()'
    );
    return undefined;
  }

  return {
    ...syncTelemetrySnapshot(),
    voipQuality: voipQualitySnapshot(),
    listeners: {
      userSessionChange: changeListenerCount(userSession()),
      tenantConfigChange: changeListenerCount(tenantConfig()),
    },
  };
}

/**
 * Install the probe. Idempotent.
 *
 * The guard reporter is wired UNCONDITIONALLY, outside the debug gate: counting must be always-on
 * so a trip that happened before anyone thought to look is still visible. Only *reading* is gated.
 */
export function installSyncTelemetryProbe(): void {
  setListenerGuardReporter(recordListenerGuardTrip);

  if (typeof window === 'undefined') {
    return;
  }

  // Never clobber an existing binding. Unlike `window.userSession` and `window.voipRtcConnection`
  // — which gate at ASSIGNMENT and are therefore simply absent in prod — this probe gates inside
  // the accessor, so the property is always defined. In web-component mode that means writing to
  // a partner dealer portal's `window`, where a same-named host global must win.
  //
  // Not un-installed on web-component teardown: this is a module-scope singleton shared by every
  // mounted component, so removing it when one disconnects would break the others.
  if ('syncTelemetry' in window) {
    return;
  }

  // @ts-expect-error - expose diagnostics in global scope, matching window.userSession/tenantConfig
  window.syncTelemetry = readSyncTelemetry;
}
